import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/admin/layout/AdminLayout';
import { adminService } from '../../../services/admin/adminService';

const CreateSubscription = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [newSubscription, setNewSubscription] = useState({
    userId: '',
    package: '1',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'active'
  });

  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');

  // Subscription packages data
  const subscriptionPackages = [
    {
      id: "1",
      name: "Basic",
      description: "Basic subscription package",
      price: 100,
      features: {
        stuff: 2,
        players: 10,
        teams: 2,
        leagues: 0,
        events: 2,
        matches: 1,
        competitions: 0,
      },
      duration: "365",
    },
    {
      id: "2",
      name: "Pro",
      description: "Pro subscription package",
      price: 200,
      features: {
        stuff: 10,
        players: 90,
        teams: 6,
        leagues: 1,
        events: 6,
        matches: 5,
        competitions: 2,
      },
      duration: "365",
    },
    {
      id: "3",
      name: "Enterprise",
      description: "Enterprise subscription package",
      price: 300,
      features: {
        stuff: 30,
        players: 300,
        teams: 15,
        leagues: 12,
        events: -1,
        matches: -1,
        competitions: 10,
      },
      duration: "365",
    },
  ];

  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOrgDropdown && !event.target.closest('.org-dropdown')) {
        setShowOrgDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOrgDropdown]);

  // Filter organizations based on search term
  const filteredOrganizations = organizations.filter(org => {
    const searchLower = orgSearchTerm.toLowerCase();
    return (
      org.fullName?.toLowerCase().includes(searchLower) ||
      org.firstName?.toLowerCase().includes(searchLower) ||
      org.lastName?.toLowerCase().includes(searchLower) ||
      org.email?.toLowerCase().includes(searchLower) ||
      org.userName?.toLowerCase().includes(searchLower)
    );
  });

  const fetchOrganizations = async () => {
    try {
      const response = await adminService.getAllUsers({ accountType: 'organization', limit: 1000 });
      setOrganizations(response.data.data.users || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setError('Failed to load organizations');
    }
  };

  const handleCreateSubscription = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      
      await adminService.updateUserSubscription(newSubscription.userId, {
        package: newSubscription.package,
        startDate: newSubscription.startDate,
        endDate: newSubscription.endDate,
        status: newSubscription.status
      });
      
      // Navigate back to subscriptions list
      navigate('/admin/subscriptions');
    } catch (error) {
      console.error('Error creating subscription:', error);
      setError(error.response?.data?.message || 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  const getPackageInfo = (packageId) => {
    return subscriptionPackages.find(pkg => pkg.id === String(packageId)) || subscriptionPackages[0];
  };

  const formatFeatures = (features) => {
    if (!features) return 'N/A';
    return Object.entries(features)
      .filter(([key, value]) => value !== 0)
      .map(([key, value]) => `${key}: ${value === -1 ? '∞' : value}`)
      .join(', ');
  };

  const getStatusBadgeClass = (status) => {
    const statusStyles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      expired: 'bg-red-100 text-red-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-blue-100 text-blue-800'
    };
    
    return statusStyles[status] || statusStyles.inactive;
  };

  const selectedPackage = getPackageInfo(newSubscription.package);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/admin/subscriptions')}
              className="flex items-center text-gray-500 hover:text-gray-700 mb-2"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back to Subscriptions
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Create New Subscription</h1>
            <p className="text-gray-600 mt-1">Set up a subscription for an organization</p>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Subscription Details</h2>
              </div>

              <form onSubmit={handleCreateSubscription} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization *
                  </label>
                  
                  {/* Custom Organization Dropdown */}
                  <div className="relative org-dropdown">
                    <button
                      type="button"
                      onClick={() => setShowOrgDropdown(!showOrgDropdown)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 bg-white text-left flex items-center justify-between"
                    >
                      {newSubscription.userId ? (
                        (() => {
                          const selectedOrg = organizations.find(org => org._id === newSubscription.userId);
                          return selectedOrg ? (
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-8 w-8 mr-3">
                                {selectedOrg.profilePicture ? (
                                  <img 
                                    src={selectedOrg.profilePicture} 
                                    alt={selectedOrg.fullName || `${selectedOrg.firstName} ${selectedOrg.lastName}`}
                                    className="h-8 w-8 rounded-full object-cover"
                                    onError={(e) => {
                                      // Fallback to initials if image fails to load
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className={`h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center ${selectedOrg.profilePicture ? 'hidden' : ''}`}>
                                  <span className="text-orange-600 font-medium text-sm">
                                    {selectedOrg.firstName?.charAt(0)?.toUpperCase() || 'O'}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {selectedOrg.fullName || `${selectedOrg.firstName} ${selectedOrg.lastName}`}
                                </div>
                                <div className="text-xs text-gray-500">{selectedOrg.email}</div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500">Select Organization</span>
                          );
                        })()
                      ) : (
                        <span className="text-gray-500">Select Organization</span>
                      )}
                      
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {/* Dropdown Menu */}
                    {showOrgDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        <div className="p-2">
                          <input
                            type="text"
                            placeholder="Search organizations..."
                            value={orgSearchTerm}
                            onChange={(e) => setOrgSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 mb-2"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        
                        <div className="max-h-48 overflow-y-auto">
                          {filteredOrganizations.map((org) => (
                            <button
                              key={org._id}
                              type="button"
                              onClick={() => {
                                setNewSubscription(prev => ({ ...prev, userId: org._id }));
                                setShowOrgDropdown(false);
                                setOrgSearchTerm(''); // Clear search when selecting
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 mr-3">
                                  {org.profilePicture ? (
                                    <img 
                                      src={org.profilePicture} 
                                      alt={org.fullName || `${org.firstName} ${org.lastName}`}
                                      className="h-10 w-10 rounded-full object-cover"
                                      onError={(e) => {
                                        // Fallback to initials if image fails to load
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <div className={`h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center ${org.profilePicture ? 'hidden' : ''}`}>
                                    <span className="text-orange-600 font-medium text-sm">
                                      {org.firstName?.charAt(0)?.toUpperCase() || 'O'}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 truncate">
                                    {org.fullName || `${org.firstName} ${org.lastName}`}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate">{org.email}</div>
                                  <div className="text-xs text-gray-400">@{org.userName}</div>
                                </div>
                                {newSubscription.userId === org._id && (
                                  <div className="flex-shrink-0 ml-2">
                                    <svg className="h-5 w-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                        
                        {filteredOrganizations.length === 0 && (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            {orgSearchTerm ? 'No organizations found matching your search' : 'No organizations found'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Error state for required field */}
                  {!newSubscription.userId && (
                    <p className="mt-1 text-sm text-red-600">Please select an organization</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subscription Package *
                  </label>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {subscriptionPackages.map((pkg) => (
                      <div
                        key={pkg.id}
                        className={`relative rounded-lg border cursor-pointer p-4 ${
                          newSubscription.package === pkg.id
                            ? 'border-orange-500 ring-2 ring-orange-500'
                            : 'border-gray-300'
                        }`}
                        onClick={() => setNewSubscription(prev => ({ ...prev, package: pkg.id }))}
                      >
                        <div className="flex items-center">
                          <input
                            type="radio"
                            name="package"
                            value={pkg.id}
                            checked={newSubscription.package === pkg.id}
                            onChange={(e) => setNewSubscription(prev => ({ ...prev, package: e.target.value }))}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                          />
                          <div className="ml-3">
                            <label className="font-medium text-gray-900 text-sm">
                              {pkg.name}
                            </label>
                            <div className="text-gray-500 text-sm">${pkg.price}/year</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={newSubscription.startDate}
                      onChange={(e) => setNewSubscription(prev => ({ ...prev, startDate: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={newSubscription.endDate}
                      onChange={(e) => setNewSubscription(prev => ({ ...prev, endDate: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status *
                  </label>
                  <select
                    value={newSubscription.status}
                    onChange={(e) => setNewSubscription(prev => ({ ...prev, status: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="expired">Expired</option>
                    <option value="suspended">Suspended</option>
                    <option value="pending">Pending</option>
                  </select>
                  {newSubscription.status && (
                    <div className="mt-2">
                      <span className="text-sm text-gray-600">Preview: </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(newSubscription.status)}`}>
                        {newSubscription.status.charAt(0).toUpperCase() + newSubscription.status.slice(1)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => navigate('/admin/subscriptions')}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !newSubscription.userId}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Creating...' : 'Create Subscription'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Package Preview Section */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Package Preview</h2>
              </div>

              <div className="p-6">
                <div className="text-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">{selectedPackage.name}</h3>
                  <div className="text-3xl font-bold text-orange-600">${selectedPackage.price}</div>
                  <div className="text-sm text-gray-500">per year</div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">Features Include:</h4>
                  
                  <div className="space-y-2">
                    {Object.entries(selectedPackage.features).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 capitalize">{key}:</span>
                        <span className="font-medium text-gray-900">
                          {value === -1 ? 'Unlimited' : value === 0 ? 'Not included' : value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Organization Preview */}
            {newSubscription.userId && (
              <div className="bg-white shadow rounded-lg mt-6">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900">Selected Organization</h2>
                </div>
                <div className="p-6">
                  {(() => {
                    const selectedOrg = organizations.find(org => org._id === newSubscription.userId);
                    return selectedOrg ? (
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {selectedOrg.profilePicture ? (
                            <img 
                              src={selectedOrg.profilePicture} 
                              alt={selectedOrg.fullName || `${selectedOrg.firstName} ${selectedOrg.lastName}`}
                              className="h-10 w-10 rounded-full object-cover"
                              onError={(e) => {
                                // Fallback to initials if image fails to load
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className={`h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center ${selectedOrg.profilePicture ? 'hidden' : ''}`}>
                            <span className="text-orange-600 font-medium text-sm">
                              {selectedOrg.firstName?.charAt(0)?.toUpperCase() || 'O'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {selectedOrg.fullName || `${selectedOrg.firstName} ${selectedOrg.lastName}`}
                          </div>
                          <div className="text-sm text-gray-500">{selectedOrg.email}</div>
                          <div className="text-xs text-gray-400">@{selectedOrg.userName}</div>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CreateSubscription; 